import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertCircle, Bell, BellOff, Check, Loader2 } from "lucide-react";

interface WebPushCardProps {
  pushSupported: boolean;
  pushPermission: NotificationPermission;
  isSubscribing: boolean;
  subscriptionError: string | null;
  preferences?: { pushEnabled?: boolean | null } | null;
  onEnable: () => void;
  onDisable: () => void;
}

export function WebPushCard({
  pushSupported,
  pushPermission,
  isSubscribing,
  subscriptionError,
  preferences,
  onEnable,
  onDisable,
}: WebPushCardProps) {
  if (!pushSupported) {
    return (
      <Card className="card-elevated border-none shadow-premium">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Push Notifications
          </CardTitle>
          <CardDescription>
            Receive instant alerts on your device when great conditions are predicted
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-3 p-4 bg-muted rounded-lg">
            <AlertCircle className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-medium">Not Supported</p>
              <p className="text-sm text-muted-foreground">
                Push notifications are not supported in this browser. Try using Chrome, Firefox, or Safari.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (pushPermission === "denied") {
    return (
      <Card className="card-elevated border-none shadow-premium">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Push Notifications
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-3 p-4 bg-destructive/10 rounded-lg">
            <BellOff className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-destructive">Permission Denied</p>
              <p className="text-sm text-muted-foreground">
                You've blocked notifications. Please enable them in your browser settings.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-elevated border-none shadow-premium">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Push Notifications
        </CardTitle>
        <CardDescription>
          Receive instant alerts on your device when great conditions are predicted
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {preferences?.pushEnabled ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Check className="h-5 w-5 text-green-600" />
                <div>
                  <p className="font-medium">Push notifications enabled</p>
                  <p className="text-sm text-muted-foreground">
                    You'll receive alerts for exceptional conditions
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={onDisable}
                disabled={isSubscribing}
                aria-busy={isSubscribing}
              >
                {isSubscribing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Disabling...
                  </>
                ) : (
                  "Disable"
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Enable push notifications</p>
                <p className="text-sm text-muted-foreground">
                  Get instant alerts when conditions are perfect
                </p>
              </div>
              <Button onClick={onEnable} disabled={isSubscribing} aria-busy={isSubscribing}>
                {isSubscribing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Subscribing...
                  </>
                ) : (
                  "Enable"
                )}
              </Button>
            </div>
            {subscriptionError && (
              <div className="flex items-start gap-3 p-4 bg-destructive/10 rounded-lg" role="alert" aria-live="polite">
                <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-destructive">Subscription Failed</p>
                  <p className="text-sm text-muted-foreground">{subscriptionError}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
